import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { Crosshair } from "@/components/Crosshair";
import { RefreshCw, Loader2, CircleCheck, CircleAlert, CircleX, CircleDashed, History, Activity, ChevronDown } from "lucide-react";

const INCIDENT_PREVIEW_COUNT = 3;
import StripeObjectAudit from "@/components/admin/StripeObjectAudit";

const SEV_DOT = { green: "bg-green-500", yellow: "bg-yellow-500", red: "bg-red-500", neutral: "bg-zinc-500" };
const LABEL = {
  operational: "Operational",
  degraded: "Degraded",
  down: "Down",
  not_configured: "Not configured",
  unknown: "Unknown",
};
const SEV_LABEL_CLS = { green: "text-green-400", yellow: "text-yellow-400", red: "text-red-400", neutral: "text-zinc-400" };
const BANNER = {
  green: { cls: "border-green-500/40 bg-green-500/10 text-green-400", Icon: CircleCheck },
  yellow: { cls: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400", Icon: CircleAlert },
  red: { cls: "border-red-500/40 bg-red-500/10 text-red-400", Icon: CircleX },
};

function fmtDuration(startIso, endIso) {
  if (!startIso) return "";
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const s = Math.max(0, Math.round((end - start) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

export default function AdminSystemStatus() {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [incidentsOpen, setIncidentsOpen] = useState(false);
  const [collapsedProbes, setCollapsedProbes] = useState(() => new Set());
  const toggleProbe = (svc) => setCollapsedProbes((prev) => {
    const next = new Set(prev);
    next.has(svc) ? next.delete(svc) : next.add(svc);
    return next;
  });
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [cur, hist] = await Promise.all([
        api.get("/admin/status/current"),
        api.get("/admin/status/history?limit=40"),
      ]);
      setData(cur.data);
      setIncidents(hist.data.incidents || []);
    } catch { /* keep prior */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const tick = () => { if (!document.hidden) load(); };
    pollRef.current = setInterval(tick, 30000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try { await api.post("/admin/status/run"); await load(); } catch { /* noop */ }
    finally { setRunning(false); }
  };

  const summary = data?.summary || { severity: "green", banner: "All systems operational", operational_label: "" };
  const banner = BANNER[summary.severity] || BANNER.green;
  const OverallIcon = banner.Icon;

  return (
    <div className="mt-6" data-testid="admin-system-status">
      <div className="border border-sniper bg-surface">
        <div className="px-4 py-3 border-b border-sniper flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2" data-testid="system-status-title">
              <Activity className="w-4 h-4 text-olive-bright" /> System Status
            </h2>
            <p className="text-[10px] font-mono uppercase text-muted-foreground mt-0.5">
              Automated checks {data?.interval_hours ? `every ${data.interval_hours}h` : ""} · integrations &amp; services
            </p>
            {data?.last_manual_run_at && (
              <p className="text-[10px] font-mono uppercase text-muted-foreground mt-0.5" data-testid="status-last-manual-run">
                Last manual run {timeAgo(data.last_manual_run_at)}{data.last_manual_run_by ? ` by ${data.last_manual_run_by}` : ""}
              </p>
            )}
            <p className="text-[10px] font-mono uppercase text-muted-foreground mt-0.5" data-testid="status-license-notifier">
              FFL renewal notifier ·{" "}
              {data?.license_notifier?.last_run_at
                ? <>last ran {timeAgo(data.license_notifier.last_run_at)} · {data.license_notifier.sent ?? 0} reminded · {data.license_notifier.lapsed ?? 0} lapsed</>
                : <span className="text-[#E8A05A]">has not run yet</span>}
            </p>
          </div>
          <button onClick={runNow} disabled={running} data-testid="status-run-now" className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-foreground font-mono uppercase text-xs px-3 py-2 transition-colors disabled:opacity-60">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Run checks now
          </button>
        </div>
        <div className="p-4">
          {loading ? (
        <div className="flex items-center gap-2 text-secondary-foreground font-mono text-sm" data-testid="status-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading status…
        </div>
      ) : (
        <>
          <div className={`flex items-center gap-3 border px-4 py-3 mb-6 ${banner.cls}`} data-testid="status-overall">
            <OverallIcon className="w-5 h-5 shrink-0" />
            <div className="min-w-0">
              <span className="font-heading text-xl uppercase tracking-tight block" data-testid="status-banner-text">{summary.banner}</span>
              <span className="font-mono text-[11px] uppercase tracking-wide opacity-80" data-testid="status-operational-count">{summary.operational_label}</span>
            </div>
            {data?.checked_at && <span className="ml-auto text-[11px] font-mono uppercase text-muted-foreground shrink-0">Updated {timeAgo(data.checked_at)}</span>}
          </div>

          <div className="border border-sniper bg-obsidian divide-y divide-sniper" data-testid="status-services">
            {(data?.services || []).map((s) => {
              const collapsed = collapsedProbes.has(s.service);
              return (
              <div key={s.service} className="flex items-center gap-3 px-4 py-3.5" data-testid={`status-service-${s.service}`}>
                <button
                  type="button"
                  onClick={() => toggleProbe(s.service)}
                  aria-expanded={!collapsed}
                  data-testid={`status-service-toggle-${s.service}`}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                  title={collapsed ? "Expand" : "Collapse"}
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                </button>
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${SEV_DOT[s.severity] || SEV_DOT.yellow}`} data-testid={`status-dot-${s.service}`} title={LABEL[s.status] || s.status} />
                <div className="min-w-0 flex-1">
                  <p className="font-body text-sm text-foreground font-semibold">{s.label}</p>
                  {!collapsed && <p className="font-mono text-[11px] text-muted-foreground truncate" data-testid={`status-detail-${s.service}`}>{s.detail}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className={`font-mono text-xs uppercase ${SEV_LABEL_CLS[s.severity] || ""}`} data-testid={`status-label-${s.service}`}>{LABEL[s.status] || s.status}</p>
                  <p className="font-mono text-[10px] text-muted-foreground">{s.checked_at ? `checked ${timeAgo(s.checked_at)}` : "not yet checked"}</p>
                </div>
              </div>
              );
            })}
          </div>

          <h3 className="font-heading text-2xl font-bold uppercase tracking-tight mt-10 mb-3 flex items-center gap-2 flex-wrap">
            <History className="w-5 h-5 text-olive-bright" /> Incident History
            {incidents.length > 0 && (
              <span className="text-[11px] font-mono uppercase text-muted-foreground border border-sniper rounded px-1.5 py-0.5" data-testid="incidents-count">{incidents.length}</span>
            )}
            {incidents.length > INCIDENT_PREVIEW_COUNT && (
              <button
                type="button"
                onClick={() => setIncidentsOpen((o) => !o)}
                aria-expanded={incidentsOpen}
                data-testid="status-incidents-toggle"
                className="ml-auto inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide text-muted-foreground hover:text-foreground border border-sniper hover:border-olive px-2.5 py-1 transition-colors"
              >
                {incidentsOpen ? "Collapse" : `Show all ${incidents.length}`}
                <ChevronDown className={`w-4 h-4 transition-transform ${incidentsOpen ? "" : "-rotate-90"}`} />
              </button>
            )}
          </h3>
          <div className="border border-sniper bg-obsidian" data-testid="status-incidents">
            {incidents.length === 0 ? (
              <p className="text-sm font-body text-muted-foreground p-6 flex items-center gap-2" data-testid="incidents-empty">
                <CircleCheck className="w-4 h-4 text-green-500" /> No incidents recorded. All services have been healthy.
              </p>
            ) : (
              <>
              <ul className="divide-y divide-sniper" data-testid="incidents-list">
                {(incidentsOpen ? incidents : incidents.slice(0, INCIDENT_PREVIEW_COUNT)).map((inc) => (
                  <li key={inc.id} className={`px-4 py-3 border-l-2 ${inc.resolved_at ? "border-l-sniper" : "border-l-red-500"}`} data-testid={`incident-${inc.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`w-2 h-2 rounded-full ${inc.status === "down" ? "bg-red-500" : inc.status === "degraded" ? "bg-yellow-500" : "bg-zinc-500"}`} />
                      <span className="font-body text-sm text-foreground font-semibold">{inc.label}</span>
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${inc.resolved_at ? "bg-white/10 text-muted-foreground" : "bg-red-500/20 text-red-400"}`}>
                        {inc.resolved_at ? "Resolved" : "Ongoing"}
                      </span>
                      <span className="ml-auto text-[11px] font-mono text-muted-foreground">
                        {fmtDuration(inc.started_at, inc.resolved_at)}
                      </span>
                    </div>
                    <p className="font-mono text-[11px] text-muted-foreground mt-1">{inc.detail}</p>
                    <p className="font-mono text-[10px] text-muted-foreground/70 mt-0.5">
                      {timeAgo(inc.started_at)}{inc.resolved_at ? ` → resolved ${timeAgo(inc.resolved_at)}` : " · still open"}
                    </p>
                  </li>
                ))}
              </ul>
              {!incidentsOpen && incidents.length > INCIDENT_PREVIEW_COUNT && (
                <button
                  type="button"
                  onClick={() => setIncidentsOpen(true)}
                  data-testid="status-incidents-showmore"
                  className="w-full px-4 py-3 border-t border-sniper text-xs font-mono uppercase tracking-wide text-olive-bright hover:bg-white/[0.03] transition-colors flex items-center justify-center gap-1.5"
                >
                  Show {incidents.length - INCIDENT_PREVIEW_COUNT} more <ChevronDown className="w-4 h-4" />
                </button>
              )}
              </>
            )}
          </div>
        </>
      )}
        </div>
      </div>
      <StripeObjectAudit />
    </div>
  );
}
